import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePermissions, ROLE_LABELS } from "@/lib/permissions";
import { cn, humanCategory } from "@/lib/util";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, LineChart, Line,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  AlertTriangle, ArrowRight, Building2, Database, EyeOff, Fingerprint,
  Flame, Globe, KeyRound, Loader2, Network, Radar, Server, ShieldCheck,
  Skull, Sparkles, TrendingDown, TrendingUp, Zap,
} from "lucide-react";
import { SeverityBadge } from "@/components/security/SeverityBadge";
import { PriorityBadge } from "@/components/security/PriorityBadge";

const SEV_COLOR = { critical: "#FF2E93", high: "#FF6B00", medium: "#F1D302", low: "#00E5FF", info: "#8B5CF6" };

// Score -> tone (higher = better posture; below thresholds = danger)
function scoreTone(score) {
  if (score >= 80) return { text: "text-sev-low",     border: "border-sev-low/50",     hex: "#00E5FF", label: "SEGURO" };
  if (score >= 60) return { text: "text-sev-medium",  border: "border-sev-medium/50",  hex: "#F1D302", label: "ATENCIÓN" };
  if (score >= 40) return { text: "text-sev-high",    border: "border-sev-high/50",    hex: "#FF6B00", label: "ALTO RIESGO" };
  return                { text: "text-sev-critical",border: "border-sev-critical/50",hex: "#FF2E93", label: "CRÍTICO" };
}

export default function Dashboard() {
  const { role, isClient } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState(() =>
    isClient ? "" : (sessionStorage.getItem("cibraxis:dashboard:client") || "")
  );

  const load = useCallback(async (orgId) => {
    setLoading(true);
    try {
      const url = orgId
        ? `/dashboard/overview?organization_id=${encodeURIComponent(orgId)}`
        : "/dashboard/overview";
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el dashboard");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(clientFilter); }, [load, clientFilter]);

  useEffect(() => {
    if (isClient) return;
    api.get("/clients").then(r => setClients(r.data || [])).catch(() => {});
  }, [isClient]);

  const onClientFilterChange = (id) => {
    setClientFilter(id);
    if (id) sessionStorage.setItem("cibraxis:dashboard:client", id);
    else sessionStorage.removeItem("cibraxis:dashboard:client");
  };

  if (loading || !data) {
    return (
      <div className="p-8 text-muted-foreground font-mono text-sm flex items-center gap-2" data-testid="dashboard-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando panel de control…
      </div>
    );
  }

  const isAggregate = data.role_scope === "aggregate";
  const activeClient = clientFilter
    ? (clients.find(c => c.id === clientFilter) || null)
    : null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* ---------- Header --------- */}
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Panel de control · {ROLE_LABELS[role] || role}</div>
          <h1 className="font-display font-bold tracking-tighter" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>
            {activeClient
              ? `Vista de ${activeClient.name}`
              : (isAggregate ? "Vista global de todos los clientes" : "Panel de mi empresa")}
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            {activeClient
              ? `Filtro activo — todos los KPI, hallazgos y cambios están limitados a ${activeClient.name}.`
              : (isAggregate
                  ? `Consolidado de ${data.client_count} cliente${data.client_count === 1 ? "" : "s"} · el más expuesto aparece en rojo arriba.`
                  : "Foco en tu empresa: exposición actual, cambios recientes y en qué mitigar primero.")}
          </p>
        </div>
        <div className="font-mono text-[10px] text-muted-foreground tracking-widest uppercase">
          Generado {new Date(data.generated_at).toLocaleString("es-ES")}
        </div>
      </header>

      {/* Cliente selector para admins/analistas */}
      {!isClient && clients.length > 0 && (
        <section className="border border-border bg-card/60 p-3 flex flex-col md:flex-row md:items-center gap-3"
                 data-testid="dashboard-client-filter">
          <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <Building2 className="w-3 h-3" /> Filtrar por cliente
          </label>
          <select
            value={clientFilter}
            onChange={(e) => onClientFilterChange(e.target.value)}
            data-testid="dashboard-client-select"
            className="flex-1 md:max-w-md bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
          >
            <option value="">— Vista global (todos los clientes) —</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {clientFilter && (
            <button
              onClick={() => onClientFilterChange("")}
              className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary"
              data-testid="dashboard-client-clear"
            >
              Limpiar filtro
            </button>
          )}
        </section>
      )}

      {/* ---------- 1. Cyber Risk Score --------- */}
      <SectionCyberRiskScore data={data} isAggregate={isAggregate} />

      {/* ---------- 2 · 3 · 4 Row: assets + vulnerabilities + exposed services --------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionAssets assets={data.assets} />
        <SectionVulnerabilities v={data.vulnerabilities} />
        <SectionExposedServices services={data.exposed_services} />
      </div>

      {/* ---------- 5 · 6 · 7 Row of placeholder intel panels --------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <IntelPlaceholder
          icon={<KeyRound className="w-4 h-4" />}
          title="Credenciales corporativas filtradas"
          section="5"
          testid="section-credentials"
          data={data.credentials_leaks}
        />
        <IntelPlaceholder
          icon={<Skull className="w-4 h-4" />}
          title="Menciones en Dark Web y ransomware"
          section="6"
          testid="section-darkweb"
          data={data.darkweb_mentions}
        />
        <IntelPlaceholder
          icon={<Flame className="w-4 h-4" />}
          title="CVEs activamente explotadas"
          section="7"
          testid="section-kev"
          data={data.kev_exploited}
          showCount
        />
      </div>

      {/* ---------- 8. Recent attack-surface changes --------- */}
      <SectionRecentChanges rc={data.recent_changes} />

      {/* ---------- 9. Risk score historical trend --------- */}
      <SectionHistoryTrend history={data.score_history} />

      {/* ---------- 10. Top priorities --------- */}
      <SectionTopPriorities items={data.top_priorities} isAggregate={isAggregate} />
    </div>
  );
}

// ============================================================================
// Section 1 · Cyber Risk Score per client (or single card if scoped client)
// ============================================================================
function SectionCyberRiskScore({ data, isAggregate }) {
  const worst = data.worst_client;
  const list = data.risk_scores || [];
  return (
    <section className="border border-border bg-card/60 p-5" data-testid="section-risk-scores">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-2 text-primary">
            <ShieldCheck className="w-3 h-3" /> 1 · Nivel de exposición
          </div>
          <h2 className="font-display text-xl font-medium mt-1">
            {isAggregate ? "Cyber Risk Score por cliente" : "Tu Cyber Risk Score"}
          </h2>
        </div>
        {worst && isAggregate && list.length > 1 && (
          <div className="border border-sev-critical/50 bg-sev-critical/5 px-4 py-2" data-testid="worst-client-banner">
            <div className="overline text-[9px] text-sev-critical">Cliente más expuesto</div>
            <div className="font-display text-sev-critical text-lg font-bold">{worst.name}</div>
            <div className="font-mono text-[10px] text-muted-foreground">
              Score {worst.score}/100 · {worst.critical} crítico{worst.critical === 1 ? "" : "s"} · {worst.high} alto{worst.high === 1 ? "" : "s"}
            </div>
          </div>
        )}
      </div>

      <div className={cn("grid gap-3", isAggregate ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1")}>
        {list.map((c) => {
          const tone = scoreTone(c.score);
          return (
            <Link to={`/clients/${c.organization_id}`}
                  key={c.organization_id}
                  data-testid={`risk-card-${c.organization_id}`}
                  className={cn("block border p-4 hover:bg-muted/20 transition-colors", tone.border)}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground tracking-wide mt-1 uppercase">
                    Criticidad · {c.criticality} · {c.assets_count} activos
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className={cn("font-display font-bold leading-none", tone.text)} style={{ fontSize: 44 }}>
                    {c.score}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground tracking-widest uppercase">
                    /100 · {c.letter}
                  </div>
                </div>
              </div>
              <div className="mt-3 h-1 bg-muted overflow-hidden">
                <div className="h-full" style={{ width: `${c.score}%`, backgroundColor: tone.hex }} />
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] font-mono">
                <div className="flex items-center gap-3">
                  <span className="text-sev-critical">{c.critical} crít</span>
                  <span className="text-sev-high">{c.high} alto</span>
                </div>
                {c.delta !== null && c.delta !== undefined && (
                  <div className={cn("flex items-center gap-1",
                    c.trend === "up" ? "text-sev-low" : c.trend === "down" ? "text-sev-critical" : "text-muted-foreground")}>
                    {c.trend === "up" && <TrendingUp className="w-3 h-3" />}
                    {c.trend === "down" && <TrendingDown className="w-3 h-3" />}
                    <span>{c.delta > 0 ? "+" : ""}{c.delta}</span>
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// ============================================================================
// Section 2 · Discovered assets
// ============================================================================
function SectionAssets({ assets }) {
  return (
    <section className="border border-border bg-card/60 p-5" data-testid="section-assets">
      <div className="overline flex items-center gap-2 text-primary">
        <Globe className="w-3 h-3" /> 2 · Activos descubiertos
      </div>
      <h2 className="font-display text-lg font-medium mt-1 mb-4">Superficie inventariada</h2>
      <div className="grid grid-cols-3 gap-3">
        <AssetBucket icon={<Globe className="w-3 h-3" />} label="Dominios" value={assets.domains} testid="asset-domains" />
        <AssetBucket icon={<Network className="w-3 h-3" />} label="Subdominios" value={assets.subdomains} testid="asset-subdomains" />
        <AssetBucket icon={<Server className="w-3 h-3" />} label="IPs públicas" value={assets.public_ips} testid="asset-ips" />
      </div>
      <Link to="/attack-surface"
            className="mt-4 inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline">
        Ver superficie completa <ArrowRight className="w-3 h-3" />
      </Link>
    </section>
  );
}

function AssetBucket({ icon, label, value, testid }) {
  return (
    <div className="border border-border p-3 hover:bg-muted/20" data-testid={testid}>
      <div className="overline flex items-center gap-1 text-muted-foreground">{icon} {label}</div>
      <div className="font-display text-3xl font-bold mt-1 leading-none">{value}</div>
    </div>
  );
}

// ============================================================================
// Section 3 · Vulnerabilities
// ============================================================================
function SectionVulnerabilities({ v }) {
  return (
    <section className="border border-sev-critical/40 bg-sev-critical/5 p-5" data-testid="section-vulnerabilities">
      <div className="overline flex items-center gap-2 text-sev-critical">
        <AlertTriangle className="w-3 h-3" /> 3 · Vulnerabilidades activas
      </div>
      <h2 className="font-display text-lg font-medium mt-1 mb-4">Críticas y altas abiertas</h2>
      <div className="grid grid-cols-2 gap-3">
        <SevBucket color="critical" label="Críticas" value={v.critical} testid="vuln-critical" />
        <SevBucket color="high"     label="Altas"    value={v.high}     testid="vuln-high" />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs font-mono">
        <div className="border border-border p-2">
          <div className="overline">KEV explotadas</div>
          <div className="text-sev-critical font-display text-xl mt-1">{v.kev}</div>
        </div>
        <div className="border border-border p-2">
          <div className="overline">Total abiertas</div>
          <div className="text-foreground font-display text-xl mt-1">{v.open_total}</div>
        </div>
      </div>
      <Link to="/findings" className="mt-4 inline-flex items-center gap-1 text-xs font-mono text-sev-critical hover:underline">
        Ver todos los hallazgos <ArrowRight className="w-3 h-3" />
      </Link>
    </section>
  );
}

function SevBucket({ color, label, value, testid }) {
  const c = color === "critical" ? "text-sev-critical border-sev-critical/50" : "text-sev-high border-sev-high/50";
  return (
    <div className={cn("border p-3 bg-background/60", c)} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={cn("font-display text-4xl font-bold mt-1 leading-none")}>{value}</div>
    </div>
  );
}

// ============================================================================
// Section 4 · Exposed services
// ============================================================================
function SectionExposedServices({ services }) {
  return (
    <section className="border border-border bg-card/60 p-5" data-testid="section-exposed-services">
      <div className="overline flex items-center gap-2 text-sev-high">
        <Radar className="w-3 h-3" /> 4 · Servicios y puertos expuestos
      </div>
      <h2 className="font-display text-lg font-medium mt-1 mb-3">Mayor riesgo</h2>
      {services.length === 0 ? (
        <div className="border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
          Sin servicios expuestos de alto riesgo detectados. La superficie está limpia por ahora.
        </div>
      ) : (
        <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="exposed-services-list">
          {services.map((s) => (
            <li key={s.id} className="border border-border p-2 text-xs font-mono hover:bg-muted/20 flex items-start justify-between gap-2"
                data-testid={`exposed-svc-${s.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-foreground truncate">{s.asset || "—"}</span>
                  {s.port && <span className="text-primary">:{s.port}</span>}
                  <span className="text-muted-foreground uppercase text-[9px] tracking-widest">{s.service || "—"}</span>
                </div>
                <div className="text-[10px] text-muted-foreground truncate mt-0.5">{s.title}</div>
                {s.cve && (
                  <div className="text-[10px] text-sev-critical mt-0.5">
                    {s.cve}{s.kev && <span className="ml-2">· KEV</span>}
                  </div>
                )}
              </div>
              <span className={cn("shrink-0 px-1.5 py-0.5 text-[10px] tracking-widest uppercase border",
                s.severity === "critical" ? "border-sev-critical/50 text-sev-critical bg-sev-critical/10"
                                          : "border-sev-high/50 text-sev-high bg-sev-high/10")}>
                {s.risk_score}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ============================================================================
// Sections 5 · 6 · 7 — placeholder intel panels
// ============================================================================
function IntelPlaceholder({ icon, title, section, data, testid, showCount = false }) {
  const partial = data.status === "partial_local";
  return (
    <section className={cn("border p-5 relative", partial ? "border-sev-medium/40 bg-sev-medium/5" : "border-dashed border-border bg-muted/10")}
             data-testid={testid}>
      <div className={cn("overline flex items-center gap-2", partial ? "text-sev-medium" : "text-muted-foreground")}>
        {icon} {section} · Feed pendiente
      </div>
      <h2 className="font-display text-lg font-medium mt-1">{title}</h2>
      {showCount && (
        <div className={cn("font-display font-bold mt-2", partial ? "text-sev-medium" : "text-muted-foreground")}
             style={{ fontSize: 48, lineHeight: 1 }}>
          {data.count}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-2">{data.message}</p>
      <div className="mt-3">
        <div className="overline text-[9px] mb-1">Fuentes listas para integrar</div>
        <div className="flex flex-wrap gap-1">
          {(data.sources_ready || []).map(s => (
            <span key={s} className="border border-border px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">{s}</span>
          ))}
        </div>
      </div>
      <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
        <EyeOff className="w-3 h-3" /> Sin API conectada
      </div>
    </section>
  );
}

// ============================================================================
// Section 8 · Recent attack-surface changes
// ============================================================================
function SectionRecentChanges({ rc }) {
  return (
    <section className="border border-primary/40 bg-primary/5 p-5" data-testid="section-recent-changes">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-2 text-primary">
            <Sparkles className="w-3 h-3" /> 8 · Cambios recientes en la superficie
          </div>
          <h2 className="font-display text-xl font-medium mt-1">Últimos {rc.days} días</h2>
        </div>
        <div className="flex gap-4 font-mono text-[10px] tracking-widest uppercase">
          <ChangeStat label="Nuevos activos" value={rc.new_assets} tone="primary" testid="change-new-assets" />
          <ChangeStat label="Nuevos servicios" value={rc.new_services} tone="primary" testid="change-new-services" />
          <ChangeStat label="Nuevos certs" value={rc.new_certificates} tone="primary" testid="change-new-certs" />
          <ChangeStat label="Δ hallazgos" value={rc.new_findings} tone="critical" testid="change-new-findings" />
          <ChangeStat label="Resueltos" value={rc.resolved_findings} tone="low" testid="change-resolved" />
        </div>
      </div>
      <div style={{ height: 140 }} data-testid="recent-changes-chart">
        <ResponsiveContainer>
          <BarChart data={rc.timeline}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                   axisLine={false} tickLine={false}
                   tickFormatter={(d) => new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short" })} />
            <YAxis hide />
            <Tooltip contentStyle={{ background: "#0F0F11", border: "1px solid #27272A", fontFamily: "IBM Plex Mono", fontSize: 11 }}
                     formatter={(v, k) => k === "delta_new" ? [`+${v} nuevos`, "Nuevos"] : [`-${v} resueltos`, "Resueltos"]}
                     labelFormatter={(l) => new Date(l).toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" })} />
            <Bar dataKey="delta_new" fill="#FF2E93" name="Nuevos" radius={[2, 2, 0, 0]} />
            <Bar dataKey="delta_resolved" fill="#00E5FF" name="Resueltos" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function ChangeStat({ label, value, tone, testid }) {
  const toneCls = tone === "critical" ? "text-sev-critical" : tone === "low" ? "text-sev-low" : "text-primary";
  return (
    <div className="text-right" data-testid={testid}>
      <div className="text-muted-foreground">{label}</div>
      <div className={cn("text-2xl font-display font-bold", toneCls)}>{value}</div>
    </div>
  );
}

// ============================================================================
// Section 9 · Score historical trend
// ============================================================================
function SectionHistoryTrend({ history }) {
  const empty = !history || history.length === 0;
  const first = !empty ? history[0].score : null;
  const last = !empty ? history[history.length - 1].score : null;
  const delta = (first !== null && last !== null) ? last - first : 0;
  const improving = delta > 1;
  const worsening = delta < -1;

  return (
    <section className="border border-border bg-card/60 p-5" data-testid="section-history-trend">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-2 text-primary">
            <Zap className="w-3 h-3" /> 9 · Evolución del score
          </div>
          <h2 className="font-display text-xl font-medium mt-1">¿Mejora o empeora tu postura?</h2>
        </div>
        {!empty && (
          <div className={cn("flex items-center gap-2 font-mono text-xs px-3 py-1.5 border",
            improving ? "border-sev-low/50 text-sev-low bg-sev-low/10"
            : worsening ? "border-sev-critical/50 text-sev-critical bg-sev-critical/10"
                        : "border-border text-muted-foreground")}>
            {improving && <TrendingUp className="w-3 h-3" />}
            {worsening && <TrendingDown className="w-3 h-3" />}
            <span>
              {improving ? "Mejorando" : worsening ? "Empeorando" : "Estable"}
              {" · "}{delta > 0 ? "+" : ""}{delta} pts
            </span>
          </div>
        )}
      </div>
      {empty ? (
        <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">
          Aún no hay suficientes puntos históricos para dibujar la tendencia. Espera al primer recomputo diario.
        </div>
      ) : (
        <div style={{ height: 220 }} data-testid="score-history-chart">
          <ResponsiveContainer>
            <AreaChart data={history}>
              <defs>
                <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                     axisLine={false} tickLine={false}
                     tickFormatter={(d) => new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short" })} />
              <YAxis domain={[0, 100]} tick={{ fill: "#71717a", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                     axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0F0F11", border: "1px solid #27272A", fontFamily: "IBM Plex Mono", fontSize: 11 }} />
              <Area type="monotone" dataKey="score" stroke="#3B82F6" strokeWidth={2} fill="url(#scoreFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

// ============================================================================
// Section 10 · Top priorities
// ============================================================================
function SectionTopPriorities({ items, isAggregate }) {
  return (
    <section className="border border-sev-high/40 bg-sev-high/5 p-5" data-testid="section-top-priorities">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-2 text-sev-high">
            <Fingerprint className="w-3 h-3" /> 10 · Prioridades de mitigación
          </div>
          <h2 className="font-display text-xl font-medium mt-1">Qué arreglar primero</h2>
        </div>
        <Link to="/findings" className="text-xs font-mono text-sev-high hover:underline inline-flex items-center gap-1">
          Ver todos <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">
          Sin hallazgos abiertos priorizables. Buen trabajo — o aún no se ha lanzado ningún escaneo.
        </div>
      ) : (
        <ol className="space-y-2" data-testid="top-priorities-list">
          {items.map((f, i) => (
              <li key={f.id}
                  className="border border-border p-3 hover:bg-muted/20 grid grid-cols-[24px_1fr_auto] gap-3 items-start"
                  data-testid={`priority-${f.id}`}>
                <div className="font-display text-2xl text-muted-foreground/60 font-bold leading-none pt-1">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="min-w-0">
                  <Link to={`/findings/${f.id}`} className="text-sm font-medium hover:text-primary">
                    {f.title}
                  </Link>
                  <div className="flex flex-wrap items-center gap-2 mt-1 font-mono text-[10px] text-muted-foreground">
                    <SeverityBadge sev={f.severity} />
                    {f.cve && <span className="text-sev-critical">{f.cve}</span>}
                    {f.kev && <span className="border border-sev-critical/60 text-sev-critical px-1 uppercase">KEV</span>}
                    {f.exploit_available && !f.kev && (
                      <span className="border border-sev-high/60 text-sev-high px-1 uppercase">exploit</span>
                    )}
                    {f.internet_exposed && (
                      <span className="border border-primary/50 text-primary px-1 uppercase">público</span>
                    )}
                    {isAggregate && f.organization_name && (
                      <span className="inline-flex items-center gap-1 text-primary">
                        <Building2 className="w-3 h-3" /> {f.organization_name}
                      </span>
                    )}
                    {f.asset && <span className="truncate">· {f.asset}</span>}
                    <span>· SLA {f.sla_days}d</span>
                    <span>· {humanCategory(f.category)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 line-clamp-2">
                    <span className="text-foreground font-medium">Mitigación:</span> {f.remediation}
                  </div>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1">
                  <PriorityBadge score={f.priority_score} label={f.priority_label} />
                  <div className="font-mono text-[10px] text-muted-foreground">
                    riesgo <span className="text-sev-high">{f.risk_score}</span>
                  </div>
                </div>
              </li>
          ))}
        </ol>
      )}
    </section>
  );
}
